import { memo } from 'react'
import { CheckIcon } from '@radix-ui/react-icons'
import clsx from 'clsx'

type ProgressStepsProps = {
  labels: readonly string[]
  currentIndex: number
  isComplete: boolean
}

export const ProgressSteps = memo(function ProgressSteps({
  labels,
  currentIndex,
  isComplete,
}: ProgressStepsProps) {
  return (
    <ol className="flex items-start" data-testid="progress-steps">
      {labels.map((label, index) => {
        const isDone = index < currentIndex || isComplete
        const isCurrent = index === currentIndex && !isComplete
        const isReached = isDone || isCurrent

        return (
          <li
            key={label}
            className="flex flex-1 flex-col items-center gap-2"
            aria-current={isCurrent ? 'step' : undefined}
            data-testid={`progress-step-${index}`}
          >
            <div className="flex w-full items-center" aria-hidden>
              <span
                className={clsx(
                  'h-0.5 flex-1',
                  index === 0 && 'invisible',
                  isReached
                    ? 'bg-black dark:bg-white'
                    : 'bg-gray-200 dark:bg-gray-700'
                )}
              />
              <span
                className={clsx(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                  isDone &&
                    'border-black bg-black dark:border-white dark:bg-white',
                  isCurrent &&
                    'border-black bg-white dark:border-white dark:bg-pebble',
                  !isReached &&
                    'border-gray-200 bg-transparent dark:border-gray-700'
                )}
              >
                {isDone ? (
                  <CheckIcon className="h-4 w-4 text-white dark:text-black" />
                ) : (
                  <span
                    className={clsx(
                      'h-2 w-2 rounded-full',
                      isCurrent
                        ? 'bg-black dark:bg-white'
                        : 'bg-gray-200 dark:bg-gray-700'
                    )}
                  />
                )}
              </span>
              <span
                className={clsx(
                  'h-0.5 flex-1',
                  index === labels.length - 1 && 'invisible',
                  isDone
                    ? 'bg-black dark:bg-white'
                    : 'bg-gray-200 dark:bg-gray-700'
                )}
              />
            </div>
            <span
              className={clsx(
                'text-center text-[11px] leading-tight',
                isReached
                  ? 'font-medium text-black dark:text-white'
                  : 'text-gray-400 dark:text-gray-500'
              )}
            >
              {label}
            </span>
          </li>
        )
      })}
    </ol>
  )
})
